import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';

const PATHWAY_LABEL = { club: 'Club', regional: 'Regional', league: 'League', national: 'National', english: 'English', disability: 'Disability', university: 'University' };

const APPARATUS_LABEL = { TRA: 'trampoline', DMT: 'double mini' };
const POLL_MS = 3000;
const FAILED = 'Could not suggest a routine just now. Please try again.';

// What the routine form is filled with, from a finished suggestion.
const formFill = (r) => {
  const tail = r.target.split(': ').pop();
  return r.passes
    ? { apparatus: 'DMT', passes: r.passes.map((p, i) => ({ name: `${tail} pass ${i + 1} (${p.start})`.slice(0, 100), skills: p.skills })) }
    : { apparatus: 'TRA', name: `${tail} routine ${r.slot}`.slice(0, 100), skills: r.skills };
};

// Coaches pick trampoline or double mini, a competition (or a section of the
// 2026 requirements) and, for trampoline, routine 1 or 2. A routine, or both
// double mini passes, is suggested from the skills the gymnast has signed off
// on that apparatus. onSuggested fills the routine form, where the coach can
// change anything before saving. Suggestions are built in the background: the
// window polls, and a coach who closes it finds the latest one when they come
// back (with a button to use it).
const RoutineSuggester = ({ gymnastId, onSuggested, defaultApparatus = 'TRA', pollMs = POLL_MS }) => {
  const [apparatus, setApparatus] = useState(defaultApparatus === 'DMT' ? 'DMT' : 'TRA');
  const [targets, setTargets] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [choice, setChoice] = useState('');
  const [slot, setSlot] = useState(1);
  // The coach's notes for the AI, e.g. "avoid Double Back (P)"
  const [notes, setNotes] = useState('');
  const [level, setLevel] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);
  const [pickedUp, setPickedUp] = useState(false); // result from an earlier visit, not yet used
  const alive = useRef(true);
  useEffect(() => () => { alive.current = false; }, []);

  // Polls a suggestion until it's finished.
  const follow = async (id, autoFill) => {
    setBusy(true);
    setError(null);
    try {
      for (;;) {
        await new Promise(r => setTimeout(r, pollMs));
        if (!alive.current) return;
        const { data } = await axios.get(`/api/routine-suggestions/${id}`);
        if (!alive.current) return;
        if (data.status === 'FAILED') { setError(data.error || FAILED); return; }
        if (data.status === 'DONE') {
          setResult(data.result);
          if (autoFill) onSuggested(formFill(data.result)); else setPickedUp(true);
          return;
        }
      }
    } catch (err) {
      if (alive.current) setError(err.response?.data?.error || FAILED);
    } finally {
      if (alive.current) setBusy(false);
    }
  };

  // Pick up today's latest suggestion for this apparatus, if there is one.
  useEffect(() => {
    let current = true;
    setResult(null);
    setPickedUp(false);
    axios.get(`/api/routine-suggestions/gymnast/${gymnastId}/latest`, { params: { apparatus } })
      .then(({ data }) => {
        if (!current || !data) return;
        setChoice(`${data.target.type}:${data.target.id}`);
        setLevel(data.target.level || '');
        if (data.slot) setSlot(data.slot);
        if (data.status === 'PENDING') follow(data.id, true);
        else if (data.status === 'DONE') { setResult(data.result); setPickedUp(true); }
      })
      .catch(() => {});
    return () => { current = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gymnastId, apparatus]);

  useEffect(() => {
    setTargets(null);
    setLoadError(null);
    axios.get(`/api/routine-suggestions/gymnast/${gymnastId}/targets`, { params: { apparatus } })
      .then(res => setTargets(res.data))
      .catch(err => setLoadError(err.response?.data?.error || 'Could not load the competitions'));
  }, [gymnastId, apparatus]);

  const isDmt = apparatus === 'DMT';
  const label = APPARATUS_LABEL[apparatus];
  const apparatusChoice = (
    <div className="gymnast-routine-editor__mode" role="radiogroup" aria-label="Suggest for">
      {['TRA', 'DMT'].map(a => (
        <label key={a}>
          <input type="radio" name="suggest-apparatus" checked={apparatus === a} onChange={() => { setApparatus(a); setChoice(''); setResult(null); setError(null); }} />
          {a === 'TRA' ? ' Trampoline' : ' Double mini (both passes)'}
        </label>
      ))}
    </div>
  );

  let blocked = null;
  if (loadError) blocked = <p className="gymnast-routines__error">{loadError}</p>;
  else if (!targets) blocked = <p className="text-muted">Loading…</p>;
  else if (!targets.available) blocked = <p className="text-muted">Routine suggestions aren't switched on yet: they need an Anthropic API key (ANTHROPIC_API_KEY) on the server.</p>;
  else if (!targets.skillCount) blocked = <p className="text-muted">No {label} skills are signed off for this gymnast yet, so there's nothing to build {isDmt ? 'passes' : 'a routine'} from.</p>;
  if (blocked) return <div className="routine-suggester">{apparatusChoice}{blocked}</div>;

  const sectionsByDoc = targets.sections.reduce((m, s) => ({ ...m, [s.document]: [...(m[s.document] || []), s] }), {});
  const eventNames = [...new Set(targets.categories.map(c => c.eventName))];

  // A requirement section covering several levels needs one chosen; some
  // levels' routines are official sets, filled in rather than built.
  const [choiceType, choiceId] = choice.split(':');
  const section = choiceType === 'section' ? targets.sections.find(s => s.id === choiceId) : null;
  const levels = section?.levels || [];
  const chosenLevel = levels.find(l => l.id === level);
  const isSet = (n) => Boolean(chosenLevel?.sets.includes(n));
  const filling = !isDmt && isSet(slot);

  const suggest = async () => {
    const [type, id] = choice.split(':');
    if (!id) return setError('Choose a competition');
    if (levels.length && !chosenLevel) return setError('Choose a level');
    const target = chosenLevel ? { type, id, level } : { type, id };
    setBusy(true);
    setError(null);
    setResult(null);
    setPickedUp(false);
    try {
      const body = isDmt ? { target, apparatus } : { target, apparatus, slot };
      if (notes.trim() && !filling) body.notes = notes.trim();
      const res = await axios.post(`/api/routine-suggestions/gymnast/${gymnastId}`, body);
      setTargets(t => ({ ...t, remaining: res.data.remaining }));
      if (res.data.status === 'DONE') {
        // A set routine comes back straight away
        setResult(res.data.result);
        onSuggested(formFill(res.data.result));
        setBusy(false);
      } else {
        await follow(res.data.id, true);
      }
    } catch (err) {
      setError(err.response?.data?.error || FAILED);
      if (err.response?.data?.limit) setTargets(t => ({ ...t, remaining: err.response.data.remaining }));
      setBusy(false);
    }
  };

  return (
    <div className="routine-suggester">
      {apparatusChoice}
      <div className="form-group">
        <label htmlFor="suggest-target">Competition</label>
        <select id="suggest-target" className="form-control" value={choice} onChange={(e) => { setChoice(e.target.value); setLevel(''); setResult(null); setError(null); }}>
          <option value="">Choose a competition…</option>
          {eventNames.map(eventName => (
            <optgroup key={eventName} label={eventName}>
              {targets.categories.filter(c => c.eventName === eventName).map(c => (
                <option key={c.id} value={`category:${c.id}`}>{c.tier}: {c.name}</option>
              ))}
            </optgroup>
          ))}
          {Object.entries(sectionsByDoc).map(([doc, sections]) => (
            <optgroup key={doc} label={`2026 requirements: ${doc}`}>
              {sections.map(s => <option key={s.id} value={`section:${s.id}`}>{PATHWAY_LABEL[s.pathway] ? `${PATHWAY_LABEL[s.pathway]}: ` : ''}{s.title}</option>)}
            </optgroup>
          ))}
        </select>
        <small className="text-muted">
          Showing what fits age {targets.age ?? 'unknown'} this year. Built from the gymnast's {targets.skillCount} signed-off {label} skills.
        </small>
      </div>

      {levels.length > 0 && (
        <div className="form-group">
          <label htmlFor="suggest-level">Level</label>
          <select id="suggest-level" className="form-control" value={level} onChange={(e) => { setLevel(e.target.value); setResult(null); setError(null); }}>
            <option value="">Choose a level…</option>
            {levels.map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
          </select>
        </div>
      )}

      {!isDmt && (
        <div className="gymnast-routine-editor__mode" role="radiogroup" aria-label="Which routine">
          <label><input type="radio" name="suggest-slot" checked={slot === 1} onChange={() => { setSlot(1); setResult(null); }} /> Routine 1 ({isSet(1) ? 'official set' : 'set'})</label>
          <label><input type="radio" name="suggest-slot" checked={slot === 2} onChange={() => { setSlot(2); setResult(null); }} /> Routine 2 ({isSet(2) ? 'official set' : 'voluntary'})</label>
        </div>
      )}

      {!filling && (
        <div className="form-group">
          <label htmlFor="suggest-notes">Notes for the suggestion (optional)</label>
          <textarea
            id="suggest-notes"
            className="form-control"
            rows={2}
            maxLength={500}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="e.g. Avoid Double Back (P), finish on a Barani"
          />
        </div>
      )}

      <div className="routine-suggester__actions">
        <button type="button" className="btn btn-secondary" onClick={suggest} disabled={busy || !choice || (levels.length > 0 && !chosenLevel) || (!filling && targets.remaining === 0)}>
          {filling ? 'Fill in the set routine' : busy ? (isDmt ? 'Building passes…' : 'Building a routine…') : result ? 'Suggest another' : (isDmt ? 'Suggest passes' : 'Suggest a routine')}
        </button>
        <small className="text-muted">{targets.remaining} of {targets.limit} suggestions left today</small>
      </div>
      {busy && (
        <p className="text-muted" aria-live="polite">
          Building {isDmt ? 'the passes' : 'a routine'}. This usually takes under a minute. You can close this window and carry on: it'll be here when you come back.
        </p>
      )}
      {error && <p className="gymnast-routines__error">{error}</p>}

      {result && (
        <div className="routine-suggester__result" aria-live="polite">
          {pickedUp && (
            <p>
              Your last suggestion, for {result.target}.{' '}
              <button type="button" className="btn btn-secondary btn-sm" onClick={() => { onSuggested(formFill(result)); setPickedUp(false); }}>
                Use this suggestion
              </button>
            </p>
          )}
          {result.cannotMeet && <p className="routine-suggester__problem"><strong>Can't meet the requirements yet:</strong> {result.cannotMeet}</p>}
          {result.problems.length > 0 && (
            <div className="routine-suggester__problem">
              <strong>Doesn't check out:</strong>
              <ul>{result.problems.map(p => <li key={p}>{p}</li>)}</ul>
            </div>
          )}
          {result.notes && <p>{result.notes}</p>}
          {result.passes ? result.passes.map((p, n) => (
            <div key={n}>
              <strong>Pass {n + 1}</strong> <span className="text-muted">(DD {Number(p.totalDD).toFixed(1)})</span>
              <ol className="routine-suggester__reasons">
                {p.skills.map((s, i) => (
                  <li key={`${s.skillId}-${i}`}>
                    <strong>{s.name}</strong> <span className="text-muted">{i === 0 ? p.start : 'dismount'}</span>
                  </li>
                ))}
              </ol>
            </div>
          )) : (
            <ol className="routine-suggester__reasons">
              {result.skills.map((s, i) => (
                <li key={`${s.skillId}-${i}`}>
                  <strong>{s.name}</strong>
                </li>
              ))}
            </ol>
          )}
          {result.warnings.length > 0 && <p className="text-muted">Check: {result.warnings.join('; ')}.</p>}
          <small className="text-muted">
            DD {Number(result.totalDD).toFixed(1)}. {result.checkedAgainstRules
              ? "Checked against this competition's rules."
              : 'Not checked automatically: compare it with the requirements before using it.'} {pickedUp ? '' : result.passes
              ? 'Pass 1 is filled in below and Pass 2 is saved with it; change anything before saving.'
              : 'The skills are filled in below; change anything before saving.'}
          </small>
        </div>
      )}
    </div>
  );
};

export default RoutineSuggester;
